import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { can } from '@/components/features/permission/can';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import { queryClient } from '@/main';
import { updateOrganizationMutation } from '@/mutations/updateOrganizationMutation';
import { getQueryKeysFactory } from '@/queries/queryKeysFactory';

export const Route = createFileRoute('/_authenticated/organization/edit')({
  component: RouteComponent,
  beforeLoad: () => {
    if (!can(['organization:update'])) {
      return redirect({ to: '/dashboard' });
    }
  },
});

const getFormSchema = (t: TFunction) =>
  z.object({
    name: z
      .string({
        required_error: t(
          'authenticated.organization.edit.form.fields.name.error.required'
        ),
      })
      .min(1, {
        message: t(
          'authenticated.organization.edit.form.fields.name.error.min'
        ),
      })
      .max(30, {
        message: t(
          'authenticated.organization.edit.form.fields.name.error.max'
        ),
      }),
    locale: z.enum(['en', 'fr']),
  });

type FormValues = z.infer<ReturnType<typeof getFormSchema>>;

function RouteComponent() {
  const { t } = useTranslation();
  const organization = useCurrentOrganization();
  const { userKeys } = getQueryKeysFactory();

  const form = useForm<FormValues>({
    resolver: zodResolver(getFormSchema(t)),
    defaultValues: {
      name: organization?.name || '',
      locale: (organization as any)?.locale ?? 'en',
    },
  });

  const { mutateAsync: updateOrganizationAsync, isPending } = useMutation({
    ...updateOrganizationMutation(organization.organizationId),
    onSuccess: async (result) => {
      if (result.isErr()) {
        toast.error(
          t('authenticated.organization.edit.toast.genericError', {
            error: result.error,
          })
        );
      } else {
        toast.success(t('authenticated.organization.edit.toast.success'));
        queryClient.refetchQueries({ queryKey: userKeys.membershipList });
      }
    },
  });

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <PageHeaderTitle>
            {t('authenticated.organization.edit.title', {
              organizationName: organization.name,
            })}
          </PageHeaderTitle>
          <PageHeaderDescription>
            {t('authenticated.organization.edit.description')}
          </PageHeaderDescription>
        </PageHeaderInfo>
      </PageHeader>

      <PageContent>
        <div className="space-y-6">
          <Card>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit((data) =>
                    updateOrganizationAsync({
                      name: data.name,
                      locale: data.locale,
                    })
                  )}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t(
                            'authenticated.organization.edit.form.fields.name.label'
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t(
                              'authenticated.organization.edit.form.fields.name.placeholder'
                            )}
                            autoComplete="off"
                            data-1p-ignore
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="locale"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('navigation.settings.language.title')}
                        </FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="en">
                                {t('navigation.settings.language.english')}
                              </SelectItem>
                              <SelectItem value="fr">
                                {t('navigation.settings.language.french')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isPending}>
                      {isPending
                        ? t('authenticated.organization.edit.form.updating')
                        : t('authenticated.organization.edit.form.submit')}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </Page>
  );
}
